'use client';

/**
 * AgentGraphCanvasCard — interactive reagraph fallback for mode8.
 *
 * When ki_ask picks a capability whose `renderer_card` is null (or whose
 * card isn't in CANVAS_CARD_REGISTRY), this card is the default
 * visualisation. It picks a graph-explore capability based on the
 * resolved entity_type and renders the returned nodes/edges as an
 * interactive force-directed graph — so the user always has SOMETHING
 * exploratory to look at, even when no purpose-built card exists.
 *
 * Substrate doctrine: the existing graph_<entity>_visualize capabilities
 * (graph_project_visualize, graph_vendor_explore, graph_design_intent_
 * visualize) already return nodes/edges. We're not parsing the dispatched
 * payload's varying shapes — we're routing through the entity's
 * established graph view. Same data, generic mount slot.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useState } from 'react';
import { Loader2, Network, AlertCircle } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useDemoAskStore } from '@/lib/stores/demo-ask-store';
import { useTurn } from '@/lib/stores/turn-context';
import { useCapabilityStore, type AvailableStrategy } from '@/lib/stores/capability-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';

interface GraphPayload {
  nodes: Array<{
    id: string;
    label: string;
    type: string;
    status?: string | null;
  }>;
  edges: Array<{
    id: string;
    source: string;
    target: string;
    type?: string;
    label?: string | null;
  }>;
}

// Compact colour palette per node type. Mirrors project-graph-reagraph's
// hues so users get visual consistency across the IPE's graph surfaces.
const NODE_COLORS: Record<string, string> = {
  project: '#4A90D9',
  rfq: '#F5A623',
  po: '#7ED321',
  vendor: '#BD10E0',
  vendor_response: '#50E3C2',
  lineitem: '#F8E71C',
  product: '#4DD0E1',
  design_intent: '#FF8A65',
  message: '#9B9B9B',
  conversation: '#FF6B6B',
};


/** Find the graph capability for a given target entity type.
 *
 *  Registry-driven: every graph_*_visualize / *_explore strategy
 *  declares `category="graph_explore"` and `target_entities=[type]`
 *  on its @bff_strategy decoration. The capability_store carries
 *  both fields (projected by list_available_strategies). One query,
 *  no hardcoded entity_type → strategy maps. New graph capabilities
 *  appear automatically when registered + synced.
 *
 *  Returns null when no `graph_explore` strategy targets the given
 *  type (e.g. po, line_item — graph layer doesn't cover them yet).
 */
function findGraphStrategy(
  strategies: AvailableStrategy[],
  targetType: string | null | undefined,
): AvailableStrategy | null {
  if (!targetType) return null;
  return (
    strategies.find(
      (s) => s.category === 'graph_explore' && s.target_entities?.includes(targetType),
    ) ?? null
  );
}


/** Resolve the picked capability to its target entity type. The picked
 *  capability's `target_entities[0]` is what the SID actually is —
 *  e.g. when the agent inherits a project sid as scope for a child-type
 *  query (intent.entity_type=rfq, entity_sid=PJ_xxx), the picked
 *  capability is project_rfqs with target_entities=["project"], and
 *  THIS is the type we ground the graph in, not intent.entity_type. */
function resolvePickedTargetType(
  strategies: AvailableStrategy[],
  pickedName: string | null | undefined,
): string | null {
  if (!pickedName) return null;
  const picked = strategies.find((s) => s.name === pickedName);
  return picked?.target_entities?.[0] ?? null;
}


export function AgentGraphCanvasCard() {
  const T = useCardTypography();
  const { resolvedTheme } = useTheme();
  // Per-turn context wins over store-latest. Without this, every
  // graph-fallback card in a stacked transcript would re-render to the
  // latest turn's entity. Falls back to the store when mounted outside
  // the transcript (mode1 sel:project paths, etc.).
  const turn = useTurn();
  const storeResponse = useDemoAskStore((s) => s.response);
  const response = turn?.response ?? storeResponse;

  const strategies = useCapabilityStore((s) => s.strategies);

  const [GraphCanvas, setGraphCanvas] = useState<any>(null);
  const [themes, setThemes] = useState<{ dark: any; light: any } | null>(null);

  // Reagraph is heavy + browser-only — load on client only, same pattern
  // project-graph-reagraph.tsx uses.
  useEffect(() => {
    import('reagraph').then((mod) => {
      setGraphCanvas(() => mod.GraphCanvas);
      setThemes({ dark: mod.darkTheme, light: mod.lightTheme });
    });
  }, []);

  // Ground the graph in the SID's actual type, not the asked-about
  // entity_type. For child-type follow-ups ("how are the rfqs" inheriting
  // project Mox), entity_sid is PJ_xxx and the picked capability targets
  // project — the graph should render the project + its RFQs, not look
  // up a non-existent rfq graph.
  const targetType = resolvePickedTargetType(strategies, response?.picked_capability);
  const graphStrategy = findGraphStrategy(strategies, targetType);
  const entityId = response?.entity_sid ?? null;
  const call =
    graphStrategy && entityId
      ? { strategy: graphStrategy.name, entityId }
      : null;

  const { data, isLoading, error } = useQuery<GraphPayload>({
    queryKey: ['agent-graph', call?.strategy, call?.entityId],
    queryFn: async () => {
      if (!call) throw new Error('no graph route');
      // graph_*_visualize handlers accept SemanticIdParams; pass the
      // sid under entity_id since the alias fix (#285) lets the shared
      // param model accept it as `semantic_id`.
      const res = await api.getStrategyData<GraphPayload>(call.strategy, {
        entity_id: call.entityId,
        semantic_id: call.entityId,
      });
      if (!res.success) throw new Error(res.error || `${call.strategy} failed`);
      return res.data;
    },
    enabled: !!call,
    staleTime: 60_000,
  });

  // Routing miss — registry has no graph_explore capability targeting
  // this entity type. Either capability_store hasn't loaded yet (will
  // re-render when it does) or the type genuinely has no graph view.
  if (!call) {
    return (
      <div className="flex items-center justify-center h-[480px] px-8 text-center">
        <div>
          <Network className="w-6 h-6 mx-auto mb-3 text-muted-foreground/40" />
          <p className={cn(T.metadata, 'text-muted-foreground')}>
            No graph_explore capability registered for type{' '}
            <span className="font-mono">{targetType ?? 'unknown'}</span>.
            {' '}Picked{' '}
            <span className="font-mono">{response?.picked_capability ?? '?'}</span>
            {' '}has no purpose-built renderer either.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading || !GraphCanvas) {
    return (
      <div className="flex items-center justify-center h-[480px]">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[480px] px-8 text-center">
        <div>
          <AlertCircle className="w-6 h-6 mx-auto mb-3 text-destructive/70" />
          <p className={cn(T.metadata, 'text-destructive')}>
            Graph fetch failed: {String((error as Error)?.message || error)}
          </p>
        </div>
      </div>
    );
  }

  const nodes = (data?.nodes ?? []).map((n) => ({
    id: n.id,
    label: n.label,
    fill: NODE_COLORS[n.type] || '#888',
    data: { type: n.type, status: n.status },
  }));
  const edges = (data?.edges ?? []).map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    label: e.label || e.type,
  }));

  if (nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-[480px] px-8 text-center">
        <p className={cn(T.metadata, 'text-muted-foreground')}>
          Graph view returned no nodes for{' '}
          <span className="font-mono">{call.entityId}</span>.
        </p>
      </div>
    );
  }

  const isDark = resolvedTheme === 'dark';
  const theme = isDark ? themes?.dark : themes?.light;

  return (
    <div className="relative w-full h-[480px]">
      <div className="absolute inset-0">
        <GraphCanvas
          nodes={nodes}
          edges={edges}
          theme={theme}
          layoutType="forceDirected2d"
          // Highlight the focus entity — same node, slightly larger.
          sizingType="default"
          actives={[call.entityId]}
        />
      </div>
      <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full bg-card/80 backdrop-blur border border-border/40">
        <p className={cn(T.metadata, 'text-foreground')}>
          {nodes.length} nodes · {edges.length} edges · fallback view via{' '}
          <span className="font-mono text-[10px]">{call.strategy}</span>
        </p>
      </div>
    </div>
  );
}
